// PaperTrail history sidebar. Versions come from GET /histories.json; CSV from GET /histories.csv.
import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import SlidingPane from "modules/5_view_helpers/web/SlidingPane";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";

import { closeHistorySidebar } from "modules/3_reducers/web/ThemeOptions";
import "./HistorySliding.scss";

const EMPTY = "—";
const IGNORED_FIELDS = ["updated_at"];

const EVENT_COLORS = {
  create: "success",
  update: "primary",
  destroy: "danger",
};

const DATE_FIELD_PATTERN = /date|_at$/i;

const isDateLike = (value, field) => {
  if (_.isBoolean(value) || _.isNumber(value)) {
    return false;
  }

  const str = String(value);
  const parseable = moment(str, moment.ISO_8601, true).isValid() || moment(str).isValid();

  if (field && DATE_FIELD_PATTERN.test(field)) {
    return parseable;
  }

  return /^\d{4}-\d{2}-\d{2}/.test(str) && parseable;
};

const formatValue = (value, field) => {
  if (_.isBoolean(value)) {
    return value ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }

  if (isDateLike(value, field)) {
    return moment(value).format("DD/MM/YYYY HH:mm");
  }

  return String(value);
};

const associationFieldName = (field) => (field.endsWith("_id") ? field.slice(0, -3) : field);

const resolveIdChange = (field, value, associationLabels) => {
  const labels = associationLabels?.[field];
  if (!labels) {
    return value;
  }

  if (_.isArray(value)) {
    return value.map((id) => labels[String(id)] ?? id);
  }

  return labels[String(value)] ?? value;
};

const FieldChange = ({ field, value, associationLabels }) => {
  const resolvedValue = resolveIdChange(field, value, associationLabels);
  const label = associationLabels?.[field] ? _.startCase(associationFieldName(field)) : _.startCase(field);

  if (_.isArray(resolvedValue)) {
    return (
      <div className="history-field-change">
        <div className="history-field-label">{label}</div>
        <div className="history-field-diff">
          <span className="history-value-old">{formatValue(resolvedValue[0], field)}</span>
          <span className="history-value-arrow">→</span>
          <span className="history-value-new">{formatValue(resolvedValue[1], field)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="history-field-change">
      <div className="history-field-label">{label}</div>
      <div className="history-field-value">{formatValue(resolvedValue, field)}</div>
    </div>
  );
};

const ChangesData = (props) => {
  const { items, ignore, child, childIgnore, sortBy, isChild } = props;

  let displayItems = _.clone(items);
  if (sortBy) {
    displayItems = _.sortBy(displayItems, (i) => i.changeset[sortBy]);
  }

  return (
    <Fragment>
      {_.map(displayItems, (item) => (
        <ChangesDataRow
          key={item.id || _.join(_.values(item.changeset))}
          sortBy={sortBy}
          isChild={isChild}
          item={item}
          ignore={ignore}
          child={child}
          childIgnore={childIgnore}
        />
      ))}
    </Fragment>
  );
};

const ChangesDataRow = (props) => {
  const { item, ignore, child, childIgnore, sortBy, isChild } = props;

  const childData = child ? _.get(item, child) : null;
  const hasChildren = child && _.isNotEmpty(childData);

  const hiddenFields = [...IGNORED_FIELDS, ...(ignore || [])];
  let fields = _.filter(_.keys(item.changeset || {}), (field) => !hiddenFields.includes(field));

  return (
    <div className={`history-changeset${isChild ? " history-changeset--nested" : ""}`}>
      {_.isEmpty(fields) ? (
        <div className="history-empty px-3 py-2">No field changes recorded.</div>
      ) : (
        _.map(fields, (field) => (
          <FieldChange
            key={field}
            field={field}
            value={item.changeset[field]}
            associationLabels={item.association_labels}
          />
        ))
      )}

      {hasChildren && (
        <div className="px-2 pb-2">
          <ChangesData items={childData} ignore={childIgnore} sortBy={sortBy} isChild child={child} childIgnore={childIgnore} />
        </div>
      )}
    </div>
  );
};

const hasVisibleChanges = (changeset) => !_.isEmpty(_.omit(changeset || {}, IGNORED_FIELDS));

const HistoryEntry = ({ version }) => {
  const eventColor = EVENT_COLORS[version.event] || "secondary";

  return (
    <div className={`history-entry history-entry--${version.event || "update"}`}>
      <div className="history-entry-marker" />
      <Card className="history-entry-card mb-0">
        <CardHeader className="history-entry-header py-2 px-3">
          <div className="d-flex align-items-center">
            <Badge color={eventColor} pill>
              {_.startCase(version.event)}
            </Badge>
          </div>
          <div className="history-entry-meta">
            <div>{moment(version.created_at).format("DD/MM/YYYY HH:mm")}</div>
            <div>{version.author_name || "Unknown author"}</div>
          </div>
        </CardHeader>
        <CardBody className="history-entry-body p-0">
          {!hasVisibleChanges(version.changeset) ? (
            <div className="history-empty px-3 py-2">No field changes recorded.</div>
          ) : (
            <div className="history-changes">
              <ChangesData items={[version]} ignore={IGNORED_FIELDS} childIgnore={IGNORED_FIELDS} />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const HistoryPaneContent = ({ item_type, item_id }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!item_type || !item_id) {
      return;
    }

    let ignore = false;

    const getHistory = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await axios.get("/histories.json", {
          params: { item_type, item_id },
        });

        if (!ignore) {
          setVersions(data || []);
        }
      } catch (requestError) {
        if (!ignore) {
          setVersions([]);
          setError(_.get(requestError, "response.data.0") || "Could not load history.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    getHistory();

    return () => {
      ignore = true;
    };
  }, [item_type, item_id]);

  const csvHref = `/histories.csv?item_type=${encodeURIComponent(item_type)}&item_id=${item_id}`;

  let body;
  if (loading) {
    body = (
      <div className="text-center mt-3">
        <Spinner type="border" color="primary" />
        <div className="mt-2 text-muted">Loading history...</div>
      </div>
    );
  } else if (error) {
    body = <Alert color="danger">{error}</Alert>;
  } else if (_.isEmpty(versions)) {
    body = <div className="history-empty">No history found.</div>;
  } else {
    body = (
      <div className="history-timeline">
        {_.map(versions, (version) => (
          <HistoryEntry key={version.id} version={version} />
        ))}
      </div>
    );
  }

  return (
    <Fragment>
      <div className="d-flex justify-content-end mb-3">
        <Button color="primary" size="sm" tag="a" href={csvHref}>
          Export CSV
        </Button>
      </div>
      {body}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  historyItem: state.ThemeOptions.historyItem,
});

const HistorySidebar = ({ historyItem, closeHistorySidebar }) => (
  <SlidingPane isOpen={!!historyItem} onRequestClose={closeHistorySidebar} title="History" width="70%">
    {historyItem && <HistoryPaneContent item_type={historyItem.item_type} item_id={historyItem.item_id} />}
  </SlidingPane>
);

export default connect(mapStateToProps, { closeHistorySidebar })(HistorySidebar);
